import React from "react";
import { BookOpenCheck, CalendarClock, Layers, Users } from "lucide-react";
import { StatsCard, StatsGrid } from "../../ui";
import { formatDate, DASH } from "../../../utils/format";
import { inDays, nextBatch, phaseCounts, plural } from "./courseDetails";

const Line = ({ children }) => <span className="tw-block">{children}</span>;

function studentsLine(headcount) {
  const parts = [];
  if (headcount.withoutBatch > 0) parts.push(`${headcount.withoutBatch} not in a batch yet`);
  if (headcount.completed > 0) parts.push(`${headcount.completed} completed`);
  if (parts.length) return parts.join(" · ");
  return headcount.active ? "Enrolled now" : "No one enrolled yet";
}

function batchesLine(counts) {
  if (counts.all === 0) return "None yet";
  if (counts.running === 0 && counts.upcoming === 0) return `All ${counts.completed} completed`;
  return [
    counts.running && `${counts.running} running`,
    counts.upcoming && `${counts.upcoming} upcoming`,
    counts.completed && `${counts.completed} completed`,
  ].filter(Boolean).join(" · ");
}

/**
 * CourseVitals — the four numbers an admin reads first: how many students the
 * course has, how many batches it runs, when the next one starts, and whether
 * it has a syllabus to plan classes from.
 *
 * A tile's icon carries colour only when its number needs attention — four
 * hues say nothing, and then the amber one says nothing either.
 *
 * Props:
 *   headcount — the `/courses/:id/headcount` resource
 *   batches   — the batch-list resource (an array)
 *   syllabus  — { loading, summary } (courseDetails.syllabusSummary)
 */
const CourseVitals = ({ headcount, batches, syllabus }) => {
  const list = batches.data || [];
  const counts = phaseCounts(list);
  const next = nextBatch(list);
  const batchesLoading = batches.loading && !batches.data;
  const h = headcount.data;

  return (
    <StatsGrid cols={4}>
      <StatsCard
        title="Students"
        icon={Users}
        iconColor="primary"
        loading={headcount.loading && !h}
        value={h ? h.active : DASH}
        subtitle={<Line>{h ? studentsLine(h) : "Could not be counted"}</Line>}
      />

      <StatsCard
        title="Batches"
        icon={Layers}
        iconColor="primary"
        loading={batchesLoading}
        value={batches.data ? counts.all : DASH}
        subtitle={<Line>{batches.data ? batchesLine(counts) : "Could not be loaded"}</Line>}
      />

      <StatsCard
        title="Next batch"
        icon={CalendarClock}
        iconColor={batches.data && !next && counts.running === 0 ? "amber" : "primary"}
        loading={batchesLoading}
        value={next ? formatDate(next.batch.startDate) : DASH}
        subtitle={<Line>{next ? `${next.batch.name} · ${inDays(next.days)}` : "None scheduled"}</Line>}
      />

      <StatsCard
        title="Syllabus"
        icon={BookOpenCheck}
        iconColor={!syllabus.loading && !syllabus.summary && !syllabus.failed ? "amber" : "primary"}
        loading={syllabus.loading}
        value={syllabus.summary ? `${syllabus.summary.count} ${syllabus.summary.unit}` : DASH}
        subtitle={(
          <Line>
            {syllabus.summary
              ? `Version ${syllabus.summary.version} · ${plural(syllabus.summary.topics, "topic")}`
              : syllabus.failed ? "Could not be loaded" : "Not set up yet"}
          </Line>
        )}
      />
    </StatsGrid>
  );
};

export default CourseVitals;
